import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";
import {
  TEMPLATE_BOOKING_REMINDERS,
  TEMPLATE_INBOUND_ROUTING,
  TRIGGER_LABELS,
} from "./templates";

const TEMPLATES = {
  none: { label: "Empty (start + end only)", definition: {
    nodes: [
      { id: "start", type: "start", data: {}, position: { x: 80, y: 80 } },
      { id: "end",   type: "end",   data: {}, position: { x: 80, y: 280 } },
    ],
    edges: [{ source: "start", target: "end" }],
  }},
  inbound_routing: { label: "Inbound call routing (Scenario 1)", definition: TEMPLATE_INBOUND_ROUTING },
  booking_reminders: { label: "Booking reminders (Scenario 2)", definition: TEMPLATE_BOOKING_REMINDERS },
};

const TRIGGER_FOR_TEMPLATE = {
  inbound_routing: "inbound_call",
  booking_reminders: "booking_created",
};

export default function CreateWorkflowModal({ isOpen, onClose, onCreated }) {
  const baseUrl = getHostDomain();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [trigger, setTrigger] = useState("inbound_call");
  const [templateKey, setTemplateKey] = useState("none");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function reset() {
    setName("");
    setDescription("");
    setTrigger("inbound_call");
    setTemplateKey("none");
    setError(null);
  }

  function onTemplateChange(key) {
    setTemplateKey(key);
    const auto = TRIGGER_FOR_TEMPLATE[key];
    if (auto) setTrigger(auto);
  }

  async function handleSubmit() {
    if (!name.trim()) { setError("Name is required"); return; }
    setSubmitting(true);
    setError(null);
    try {
      const tmpl = TEMPLATES[templateKey] || TEMPLATES.none;
      const res = await fetchController(`${baseUrl}/api/v1/workflow`, "POST", {
        name: name.trim(),
        description: description.trim() || null,
        trigger,
        status: "active",
        definition: tmpl.definition,
        priority: 100,
      });
      const created = res?.data;
      reset();
      if (created) onCreated?.(created);
    } catch (e) {
      setError(e?.message || "Failed to create workflow");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={() => { reset(); onClose(); }} size="md" closeOnOverlayClick={!submitting}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>New Workflow</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isInvalid={!!error && !name.trim()} isRequired>
                <FormLabel>Name</FormLabel>
                <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
                {!!error && !name.trim() && <FormErrorMessage>{error}</FormErrorMessage>}
              </FormControl>

              <FormControl>
                <FormLabel>Trigger</FormLabel>
                <Select value={trigger} onChange={(e) => setTrigger(e.target.value)}>
                  {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </Select>
                <FormHelperText>What event fires this workflow.</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Starting template</FormLabel>
                <Select value={templateKey} onChange={(e) => onTemplateChange(e.target.value)}>
                  {Object.entries(TEMPLATES).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  placeholder="Optional"
                />
              </FormControl>

              {error && name.trim() && (
                <FormControl isInvalid><FormErrorMessage>{error}</FormErrorMessage></FormControl>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => { reset(); onClose(); }} isDisabled={submitting}>
              Cancel
            </Button>
            <Button type="button" colorScheme="blue" isLoading={submitting} onClick={handleSubmit}>
              Create
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
